/**
 * DesignCardModal — «Просмотр макета».
 *
 * Устройство окна: шапка (кто это + навигация) → полоса «Что дальше» (главное
 * действие текущей стадии) → слева просмотр макета, справа пять вкладок →
 * футер сохранения. Каждое место отвечает на один вопрос человека: «что это»,
 * «что мне с этим делать», «как выглядит», «какие данные», «сохранил ли я».
 *
 * Что изменилось против прежней версии (переработка 06.08.2026):
 * — действия конвейера подняты из вкладки воронок в полосу под шапкой: раньше
 *   кнопка «Одобрить публикацию» жила внутри списка карточек МП и исчезала,
 *   когда карточек ещё нет, то есть ровно на стадии одобрения;
 * — вкладок 7 → 5: «Связи» дублировали «Товары», «Воронки» показывали карточки
 *   МП, «Контент и ИИ» — два поля, которые логичнее править рядом с прочими;
 * — «Файлы для печати» перестали быть заглушкой и показывают реальные файлы;
 * — состояние макета производное (design_state), человек ставит только ручной
 *   флаг «приостановлен / архив».
 *
 * С 11.10.2026 рамка окна — общий каркас сущности (`EntityModalShell`): шапка
 * с кодом, бейджами и служебными номерами, цепочка «Тематика › Макет › Товары ›
 * ВБ · ОЗ · ЯМ · Сайт», полоса «Что дальше» с чипами проверок, колонка 19rem,
 * вкладки и футер. Вкладки остаются отдельными — «Атрибуты», «Файлы для печати»,
 * «Товары», «Карточки МП», «История»; каркас лишь берёт на себя то, что одинаково
 * у всех окон (оверлей, фокус, ←/→/Esc/Ctrl+S/Alt+←, «Во весь экран»).
 * Защита несохранённых правок, ‹N из M› с префетчем соседей и deep-link
 * `?design=` — прежние.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import {
  Copy,
  Files,
  ImageOff,
  Link as LinkIcon,
  Palette,
  Plus,
  Replace,
  RefreshCw,
  Sparkles,
  Trash2,
  X,
  ZoomIn,
} from 'lucide-react'
import { Badge } from '../../../components/ui/Badge'
import { Button } from '../../../components/ui/Button'
import { ConfirmDialog } from '../../../components/ui/ConfirmDialog'
import { EntityChain, type ChainItem } from '../../../components/ui/EntityChain'
import { IdMeta } from '../../../components/ui/IdMeta'
import { Lightbox } from '../../../components/ui/Lightbox'
import { Modal } from '../../../components/ui/Modal'
import type { NextStepAction } from '../../../components/ui/NextStepBar'
import { copyToClipboard as copyTextToClipboard } from '../../../components/ui/copyToClipboard'
import {
  EntityModalShell,
  type EntityMoreMenuItem,
} from '../../../components/entity-view/EntityModalShell'
import { useEntityNavigator } from '../../../components/entity-view/navigatorContext'
import { useEntityShellLayout } from '../../../components/entity-view/layoutContext'
import { marketplaceProfile, normalizeMarketplace } from '../../../shared/marketplaces'
import { Skeleton } from '../../../components/ui/Skeleton'
import { UnderlineTabs } from '../../../components/ui/UnderlineTabs'
import { useToastStore } from '../../../stores/toastStore'
import { useAuthStore } from '../../../stores/authStore'
import { api } from '../../../shared/api/client'
import { openAuthorizedMedia } from '../../../shared/media/authorizedMedia'
import { getMediaUrl } from '../../../utils/mediaUtils'
import { cn } from '../../../lib/cn'
import { designsV2API } from '../api'
import {
  pipelineAPI,
  type MarketplaceCardDto,
  type MarketplaceFieldLabels,
  type RetryDesignResult,
} from '../../pipeline/api'
import { parseFunnelCoverage } from '../../pipeline/funnelCoverage'
import { retryPublicationDecision } from '../../pipeline/publicationApproval'
import { useCoverageNotice } from '../../pipeline/useCoverageNotice'
import { attributesAPI } from '../../attributes/api'
import { entityRelationsAPI } from '../../entity-relations/api'
import type { RelationProduct } from '../../entity-relations/types'
import type { SeoDictionaryField } from '../../pipeline-rules/api'
import {
  designStateLabel,
  designStateVariant,
  designTitle,
  formatDateTime,
  pipelineStageInfo,
  tagChipClass,
  techChips,
} from '../lib'
import type {
  DesignProductionFiles,
  DesignTag,
  DesignV2,
  ProductionFile,
  ProductionFileVariant,
} from '../types'
import { TagPicker } from './TagPicker'
import { EditDesignSourceModal } from './EditDesignSourceModal'
import { AiEditDesignModal } from './AiEditDesignModal'
import { RegenerateVisualsWizard } from './RegenerateVisualsWizard'
import { RegenerateSeoWizard } from './RegenerateSeoWizard'
import { AiLayoutSourceNote } from './design-card/AiLayoutSourceNote'
import { AttributesTab } from './design-card/AttributesTab'
import { HistoryTab } from './design-card/HistoryTab'
import { MarketplaceTab, type ProductVisuals } from './design-card/MarketplaceTab'
import {
  PrintFilesTab,
  printFilePreviewSource,
  productionFileVariantUrl,
} from './design-card/PrintFilesTab'
import { ProductsTab, type KitSummary, type ProductArticleMeta } from './design-card/ProductsTab'
import { SafeImg, SEC } from './design-card/parts'
import { StageActionBar } from './design-card/StageActionBar'
import {
  STANDALONE_ACTIONS,
  stagePlan,
  type StageAction,
  type StageContext,
  type StagePlan,
} from './design-card/stageActions'
import { mapPipelineEvent, type HistoryEvent } from './design-card/events'
import { buildDesignChecks, techSummary, type DesignTabKey } from './design-card/designChecks'
import {
  PRESENCE_CHANNELS,
  PRESENCE_CHANNEL_LABEL,
  channelAggregates,
} from './design-card/designPresence'
import { isBlocked } from './design-card/marketplaceCardState'
import type { OverviewFilter } from './design-card/MarketplaceOverview'

type CardTab = DesignTabKey

const HEADER_STAGE_ACTION_KINDS = new Set<StageAction['kind']>([
  'regenerate',
  'regenerateProduction',
])

export function splitDesignCardStageActions(plan: StagePlan): {
  inlinePlan: StagePlan
  headerActions: StageAction[]
} {
  const headerActions = plan.secondary.filter((action) => HEADER_STAGE_ACTION_KINDS.has(action.kind))
  return {
    inlinePlan: {
      ...plan,
      secondary: plan.secondary.filter((action) => !HEADER_STAGE_ACTION_KINDS.has(action.kind)),
    },
    headerActions,
  }
}

export type PrintFilePreviewDecision =
  | {
      kind: 'lightbox'
      items: Array<{ src: string; label: string }>
      index: number
    }
  | {
      kind: 'fallback'
      source: string | null
    }

/**
 * Собирает единый просмотрщик по всем веткам всех печатных файлов. В ключе
 * намеренно только стабильные поля ответа API: id товара и назначение ветки.
 */
export const resolvePrintFilePreview = (
  files: ProductionFile[],
  activeFile: ProductionFile,
  activeVariant: ProductionFileVariant,
): PrintFilePreviewDecision => {
  const previewable = files.flatMap((file) =>
    file.variants.flatMap((variant) => {
      const src = printFilePreviewSource(file, variant)
      if (!src) return []
      return [
        {
          key: `${file.id}:${variant.purpose}`,
          src,
          label: [file.article, file.fileName, variant.purpose].filter(Boolean).join(' · '),
        },
      ]
    }),
  )
  const activeKey = `${activeFile.id}:${activeVariant.purpose}`
  const index = previewable.findIndex((item) => item.key === activeKey)

  if (index >= 0) {
    return {
      kind: 'lightbox',
      items: previewable.map(({ src, label }) => ({ src, label })),
      index,
    }
  }

  return {
    kind: 'fallback',
    source: productionFileVariantUrl(activeFile, activeVariant, true),
  }
}

const CARD_TABS: Array<{ key: CardTab; label: string }> = [
  { key: 'attrs', label: 'Атрибуты' },
  { key: 'printFiles', label: 'Файлы для печати' },
  { key: 'products', label: 'Товары' },
  { key: 'marketplace', label: 'Карточки МП' },
  { key: 'history', label: 'История' },
]

export type DesignCardRetryRoute =
  | { kind: 'publication_disabled'; message: string; approvalAction: null }
  | { kind: 'retry_started'; message: string; approvalAction: null }

/**
 * Повтор шага публикации требует нового подтверждения выкладки наружу. С 04.10.2026 карточки из макета не
 * публикуются (решение владельца: все выгрузки — через «Черновики ПИМ»), поэтому окно подтверждения не
 * открывается, а человек получает объяснение, где выкладывать.
 */
export function routeDesignCardRetry(
  result: RetryDesignResult,
  designCode: string,
): DesignCardRetryRoute {
  if (retryPublicationDecision(result) === 'confirm_publication') {
    return {
      kind: 'publication_disabled',
      message: `Публикация из макета #${designCode} отключена: карточки выкладываются из «Черновиков ПИМ» в каталогах площадок`,
      approvalAction: null,
    }
  }
  return {
    kind: 'retry_started',
    message: 'Повтор шага запущен',
    approvalAction: null,
  }
}

/** Стадии, на которых backend отдаёт визуалы товаров вместе со строкой конвейера */
const STAGES_WITH_VISUALS = new Set([
  'on_approval',
  'publishing',
  'awaiting_sales_start',
  'selling',
])

interface DesignCardModalProps {
  isOpen: boolean
  designCode: number | null
  /** Коды текущей страницы для навигации ‹ › */
  codes: number[]
  onNavigate: (code: number) => void
  onClose: () => void
  onSaved: () => void
  onDelete: (design: DesignV2) => void
  onDuplicate: (design: DesignV2) => void
  /** Открывает тематику окном ПОВЕРХ карточки — карточка остаётся открытой */
  onOpenTheme: (themeNumber: number) => void
  /** Открывает товар окном поверх карточки */
  onOpenProduct: (article: string) => void
  /**
   * Меняется, когда карточку нужно перечитать из-за события снаружи —
   * например после того, как ИИ записал предложенные атрибуты.
   */
  refreshToken?: number
  tags: DesignTag[]
  tagsAvailable: boolean
  onCreateTag: (value: string, color: string) => Promise<DesignTag | null>
  onOpenAi: (design: DesignV2) => void
  /** Окно открыто навигатором по цепочке: есть куда вернуться (кнопка и Alt+←). */
  canGoBack?: boolean
  onBack?: () => void
}

const DICTIONARY_FIELDS: SeoDictionaryField[] = [
  'type',
  'category',
  'brand',
  'gender',
  'age',
  'occasion',
]

/** Строковые поля формы, уходящие в PUT как есть */
const FORM_STRING_FIELDS = [
  'type',
  'category',
  'brand_ru',
  'gender',
  'age',
  'occasion',
  'lifecycle_flag',
  'name_on_image',
  'age_on_image_unit',
  'language',
  'image_subject',
  'image_text',
] as const

const FORM_FIELD_LABELS: Record<string, string> = {
  type: 'Тип макета',
  category: 'Категория',
  brand_ru: 'Бренд',
  gender: 'Пол аудитории',
  age: 'Возраст аудитории',
  occasion: 'Повод',
  lifecycle_flag: 'Ручной флаг состояния',
  name_on_image: 'Имя на макете',
  age_on_image: 'Возраст на макете',
  age_on_image_unit: 'Единица возраста',
  language: 'Язык надписи',
  image_subject: 'Что изображено',
  image_text: 'Текст на изображении',
}

/** Diff формы против исходных значений — единый источник для dirty-state и сохранения */
function computeUpdates(
  form: Record<string, string>,
  initialForm: Record<string, string>,
): Record<string, unknown> {
  const updates: Record<string, unknown> = {}
  for (const field of FORM_STRING_FIELDS) {
    if ((form[field] || '') !== (initialForm[field] || '')) {
      // Пустой ручной флаг означает «считать автоматически», а не пустую строку
      updates[field] = field === 'lifecycle_flag' && !form[field] ? null : form[field]
    }
  }
  if ((form.age_on_image || '') !== (initialForm.age_on_image || '')) {
    updates.age_on_image = form.age_on_image ? Number(form.age_on_image) : null
  }
  return updates
}

const MEDIA_TYPE_RU: Record<string, string> = { pdf: 'PDF', photo: 'Фото', image: 'JPG' }

const TECH_TONE_CLASSES = {
  ok: 'bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-300',
  warn: 'bg-red-100 text-red-700 dark:bg-red-950 dark:text-red-300',
  none: 'bg-gray-100 text-gray-500 dark:bg-zinc-800 dark:text-zinc-400',
} as const

interface BlockedMarketplaceTarget {
  article: string
  marketplace: string
  sellerId: string
  reason: string
}

/** Название площадки для текста ошибки — из реестра площадок; незнакомый код — заглавными. */
const marketplaceDisplayName = (value: string): string => {
  const code = normalizeMarketplace(value)
  return code ? marketplaceProfile(code).fullName : value.toUpperCase()
}

/**
 * Содержимое колонки узнаёт ширину окна у каркаса: колонка собирается в самом
 * окне макета, а контекст раскладки доступен только внутри каркаса.
 */
const ShellLayoutProbe: React.FC<{ children: (narrow: boolean) => React.ReactNode }> = ({ children }) => (
  <>{children(useEntityShellLayout().narrow)}</>
)

function joinRussian(values: string[]): string {
  if (values.length < 2) return values[0] || ''
  return `${values.slice(0, -1).join(', ')} и ${values.at(-1)}`
}

function parseBlockedMarketplaceTargets(value: string): BlockedMarketplaceTarget[] {
  const marker = 'MARKETPLACE_DRAFT_NOT_PUBLISHABLE:'
  const markerIndex = value.indexOf(marker)
  if (markerIndex < 0) return []

  return value
    .slice(markerIndex + marker.length)
    .split(';')
    .map((entry) => entry.trim())
    .filter(Boolean)
    .map((entry) => {
      const match = entry.match(/^([^@\s:;]+)@([^/\s:;]+)\/([^:\s;]+):\s*(.+)$/)
      if (!match) return null
      return {
        article: match[1],
        marketplace: match[2].toLowerCase(),
        sellerId: match[3],
        reason: match[4],
      }
    })
    .filter((target): target is BlockedMarketplaceTarget => target !== null)
}

function formatBlockedMarketplaceTargets(targets: BlockedMarketplaceTarget[]): string {
  const grouped = new Map<string, { article: string; marketplace: string; sellerIds: string[] }>()
  for (const target of targets) {
    const key = `${target.article}\u0000${target.marketplace}`
    const current = grouped.get(key) || {
      article: target.article,
      marketplace: target.marketplace,
      sellerIds: [],
    }
    if (!current.sellerIds.includes(target.sellerId)) current.sellerIds.push(target.sellerId)
    grouped.set(key, current)
  }

  return Array.from(grouped.values())
    .map(({ article, marketplace, sellerIds }) => {
      const marketplaceName = marketplaceDisplayName(marketplace)
      const cabinetNumbers = joinRussian(sellerIds.map((sellerId) => `№${sellerId}`))
      const cabinets = sellerIds.length === 1 ? `кабинет ${cabinetNumbers}` : `кабинеты ${cabinetNumbers}`
      return `${article} — ${marketplaceName}, ${cabinets}`
    })
    .join('; ')
}

function actionablePipelineError(value: string): string | null {
  const targets = parseBlockedMarketplaceTargets(value)
  const unpublishedVisualTargets = targets.filter((target) =>
    target.reason.includes('PIPELINE_VISUALS_NOT_PUBLISHED'),
  )
  if (unpublishedVisualTargets.length === 0) return null

  return 'Изображения для карточек созданы, но не сохранены в публичный каталог, поэтому маркетплейс не сможет их скачать: '
    + `${formatBlockedMarketplaceTargets(unpublishedVisualTargets)}. `
    + 'Проверьте доступность публичного каталога и нажмите «Повторить шаг».'
}

/**
 * pipeline_error приходит и текстом, и JSON-объектом движка вида
 * {cabinet, position, item_id, error}. Показываем человеку сообщение, а
 * технические координаты сворачиваем в короткий хвост.
 */
function readableError(raw?: string | null): string {
  const value = String(raw || '').trim()
  const actionable = actionablePipelineError(value)
  if (actionable) return actionable
  if (!value.startsWith('{')) return value
  try {
    const parsed = JSON.parse(value)
    const message = String(parsed.error || parsed.message || '').trim()
    if (!message) return value
    const where = [
      parsed.cabinet ? `кабинет ${String(parsed.cabinet).toUpperCase()}` : '',
      parsed.article ? String(parsed.article) : '',
    ]
      .filter(Boolean)
      .join(', ')
    return where ? `${message} (${where})` : message
  } catch {
    return value
  }
}

export function designPipelineNotice(
  pipelineError?: string | null,
  pipelineStatus?: string | null,
): { kind: 'error' | 'warning' | 'returned'; text: string } | null {
  const value = String(pipelineError || '').trim()
  if (!value) return null
  if (pipelineStatus === 'returned_to_designer') return { kind: 'returned', text: value }
  if (value.startsWith('SEO_TEXT_PARTIAL:')) {
    return {
      kind: 'warning',
      text: value.slice('SEO_TEXT_PARTIAL:'.length).trim() || value,
    }
  }
  return { kind: 'error', text: readableError(value) }
}

export const DesignCardModal: React.FC<DesignCardModalProps> = ({
  isOpen,
  designCode,
  codes,
  onNavigate,
  onClose,
  onSaved,
  onDelete,
  onDuplicate,
  onOpenTheme,
  onOpenProduct,
  refreshToken = 0,
  tags,
  tagsAvailable,
  onCreateTag,
  onOpenAi,
  canGoBack,
  onBack,
}) => {
  const showToast = useToastStore((s) => s.showToast)
  const permissions = useAuthStore((state) => state.user?.permissions || [])
  const userRole = useAuthStore((state) => state.user?.role)
  const canRegenerateSeo = permissions.includes('Seo:UPDATE')
  const canRenameTheme = permissions.includes('Product:UPDATE')
  const canResendPhotos = permissions.includes('Card:UPDATE')
  // Позиции без контент-воронки карточку не получат — приёмка о них молчит
  const notifyCoverage = useCoverageNotice()
  const entityNav = useEntityNavigator()
  const [tab, setTab] = useState<CardTab>('attrs')
  const [design, setDesign] = useState<DesignV2 | null>(null)
  const [loading, setLoading] = useState(false)
  const [form, setForm] = useState<Record<string, string>>({})
  const [initialForm, setInitialForm] = useState<Record<string, string>>({})
  const [dictionaries, setDictionaries] = useState<Record<string, string[]>>({})
  const [designTags, setDesignTags] = useState<DesignTag[]>([])
  const [saving, setSaving] = useState(false)

  // Данные вкладок: null — ещё не загружали
  const [printFiles, setPrintFiles] = useState<DesignProductionFiles | null>(null)
  const [products, setProducts] = useState<RelationProduct[] | null>(null)
  const [productsError, setProductsError] = useState<string | null>(null)
  const [cards, setCards] = useState<MarketplaceCardDto[] | null>(null)
  /** Подписи полей карточки — приходят вместе с карточками, из справочника площадки. */
  const [cardFieldLabels, setCardFieldLabels] = useState<MarketplaceFieldLabels | null>(null)
  const [visuals, setVisuals] = useState<ProductVisuals[] | null>(null)
  const [events, setEvents] = useState<HistoryEvent[] | null>(null)

  /** Отложенное действие (закрытие/навигация) при несохранённых изменениях */
  const [pendingLeave, setPendingLeave] = useState<(() => void) | null>(null)
  /** Лайтбокс: набор изображений и позиция */
  const [lightbox, setLightbox] = useState<{
    items: Array<{ src: string; label?: string }>
    index: number
  } | null>(null)
  /** Действие конвейера, ждущее подтверждения */
  const [pendingAction, setPendingAction] = useState<StageAction | null>(null)
  /** Комментарий для возврата дизайнеру */
  const [returnComment, setReturnComment] = useState('')
  const [actionBusy, setActionBusy] = useState<string | null>(null)
  const [editSourceOpen, setEditSourceOpen] = useState(false)
  const [aiEditOpen, setAiEditOpen] = useState(false)
  /** Мастер перегенерации визуалов: товары → кабинеты → воронки → выполнение */
  const [regenerateWizardOpen, setRegenerateWizardOpen] = useState(false)
  /** Мастер перегенерации SEO: тематика → предпросмотр → запись черновиков */
  const [regenerateSeoWizardOpen, setRegenerateSeoWizardOpen] = useState(false)
  /** Фильтр площадки для вкладки «Карточки МП»: клик по ВБ · ОЗ · ЯМ в цепочке. nonce пересоздаёт вкладку. */
  const [marketFilter, setMarketFilter] = useState<{ value: OverviewFilter; nonce: number }>({
    value: 'all',
    nonce: 0,
  })

  /** Префетч соседних макетов: code → promise данных (read-once) */
  const prefetchCache = useRef(new Map<number, Promise<DesignV2>>())

  const index = designCode == null ? -1 : codes.indexOf(designCode)

  const dirtyFieldKeys = useMemo(
    () => new Set(design ? Object.keys(computeUpdates(form, initialForm)) : []),
    [design, form, initialForm],
  )
  const dirtyFields = useMemo(
    () => [...dirtyFieldKeys].map((field) => FORM_FIELD_LABELS[field] || field),
    [dirtyFieldKeys],
  )
  const dirtyCount = dirtyFields.length

  const resetTabData = useCallback(() => {
    setPrintFiles(null)
    setProducts(null)
    setProductsError(null)
    setCards(null)
    setVisuals(null)
    setEvents(null)
  }, [])

  const load = useCallback(
    async (code: number) => {
      setLoading(true)
      resetTabData()
      try {
        const cached = prefetchCache.current.get(code)
        prefetchCache.current.delete(code)
        const data = cached ? await cached : await designsV2API.getDesign(code)
        setDesign(data)
        setDesignTags(Array.isArray(data?.tags) ? data.tags : [])
        const nextForm = {
          type: data?.type || '',
          category: data?.category || '',
          brand_ru: data?.brand_ru || '',
          gender: data?.gender || '',
          age: data?.age || '',
          occasion: data?.occasion || '',
          lifecycle_flag: data?.lifecycle_flag || '',
          name_on_image: data?.name_on_image || '',
          age_on_image: data?.age_on_image != null ? String(data.age_on_image) : '',
          age_on_image_unit: data?.age_on_image_unit || '',
          language: data?.language || '',
          image_subject: data?.image_subject || '',
          image_text: data?.image_text || '',
        }
        setForm(nextForm)
        setInitialForm({ ...nextForm })
      } catch (e: any) {
        setDesign(null)
        useToastStore
          .getState()
          .showToast(`Не удалось загрузить макет: ${e?.message || 'ошибка'}`, 'error')
      } finally {
        setLoading(false)
      }
    },
    [resetTabData],
  )

  useEffect(() => {
    if (isOpen && designCode != null) void load(designCode)
  }, [isOpen, designCode, load])

  // Перечитать карточку, когда данные изменил кто-то снаружи: ИИ пишет
  // предложенные атрибуты своим PUT, и без этого форма осталась бы со старыми
  // значениями, а сохранение из карточки затёрло бы записанное.
  useEffect(() => {
    if (!isOpen || designCode == null || refreshToken === 0) return
    // Форма перечитывается целиком, поэтому о потерянных правках говорим вслух:
    // молча подменить значения под руками — худшее, что можно сделать.
    if (dirtyCount > 0) {
      showToast('Карточка перечитана: значения изменились снаружи, правки сброшены', 'info')
    }
    void load(designCode)
    // load стабилен, но пересоздаётся вместе с resetTabData — цепляемся за токен
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshToken])

  // Префетч соседних макетов для мгновенной навигации ‹ ›
  useEffect(() => {
    if (!isOpen || index < 0) return
    for (const code of [codes[index + 1], codes[index - 1]]) {
      if (code == null || prefetchCache.current.has(code)) continue
      const promise = designsV2API.getDesign(code)
      promise.catch(() => prefetchCache.current.delete(code))
      prefetchCache.current.set(code, promise)
    }
  }, [isOpen, index, codes])

  // Сброс кэша и отложенных действий при закрытии
  useEffect(() => {
    if (!isOpen) {
      prefetchCache.current.clear()
      setPendingLeave(null)
      setPendingAction(null)
    }
  }, [isOpen])

  /** Выполнить действие сразу или после подтверждения, если есть правки */
  const guardLeave = useCallback(
    (action: () => void) => {
      if (dirtyCount > 0) setPendingLeave(() => action)
      else action()
    },
    [dirtyCount],
  )

  useEffect(() => {
    if (!isOpen || Object.keys(dictionaries).length) return
    let cancelled = false
    attributesAPI
      .getAll()
      .then((items) => {
        if (cancelled) return
        const next: Record<string, string[]> = {}
        for (const field of DICTIONARY_FIELDS) {
          next[field] = [
            ...new Set(
              items
                .filter((item) => item.field === field)
                .sort((a, b) => a.sort - b.sort || a.value.localeCompare(b.value, 'ru'))
                .map((item) => item.value),
            ),
          ]
        }
        setDictionaries(next)
      })
      .catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [isOpen, dictionaries])

  // Товары и карточки МП — сразу при открытии: их счётчики стоят на вкладках, а
  // полоса действий по ним решает, что предложить человеку.
  useEffect(() => {
    if (!isOpen || designCode == null || products !== null) return
    let cancelled = false
    void entityRelationsAPI
      .get({ designCode: String(designCode) })
      .then((relations) => {
        if (!cancelled) setProducts(relations?.products || [])
      })
      .catch((e: any) => {
        if (cancelled) return
        setProducts([])
        setProductsError(
          e?.response?.data?.error?.message || e?.message || 'Не удалось загрузить товары',
        )
      })
    return () => {
      cancelled = true
    }
  }, [isOpen, designCode, products])

  useEffect(() => {
    if (!isOpen || designCode == null || cards !== null) return
    let cancelled = false
    ;(async () => {
      try {
        // Боевые карточки из wb_products/ozon_products по артикулу — тот же
        // источник, по которому считается состояние макета. Прежний GET /cards
        // читал marketplace_cards (каркас Этапа 6, 111 строк на весь каталог),
        // и продающийся макет показывал пустую вкладку.
        const response = await api.get(`/designs/${encodeURIComponent(String(designCode))}/marketplace-cards`)
        const data = response.data?.data ?? response.data ?? {}
        const items = Array.isArray(data.items) ? data.items : Array.isArray(data) ? data : []
        if (!cancelled) {
          setCards(items)
          setCardFieldLabels(data.field_labels ?? null)
        }
      } catch {
        if (!cancelled) {
          setCards([])
          setCardFieldLabels(null)
        }
      }
    })()
    return () => {
      cancelled = true
    }
  }, [isOpen, designCode, cards])

  // Визуалы товаров backend отдаёт только вместе со строкой конвейера и только
  // на стадиях, где они уже существуют — на прочих запрос был бы пустым.
  useEffect(() => {
    if (!isOpen || designCode == null || visuals !== null || !design) return
    if (!STAGES_WITH_VISUALS.has(design.pipeline_status || '')) {
      setVisuals([])
      return
    }
    let cancelled = false
    void pipelineAPI
      .getPipeline({ stage: design.pipeline_status, search: String(designCode), limit: 1 })
      .then((response) => {
        if (cancelled) return
        const item = response.items.find((row) => String(row.design_code) === String(designCode))
        setVisuals((item?.products || []) as ProductVisuals[])
      })
      .catch(() => {
        if (!cancelled) setVisuals([])
      })
    return () => {
      cancelled = true
    }
  }, [isOpen, designCode, visuals, design])

  // Файлы для печати — сразу: от них зависят счётчик на вкладке и колонка
  // «Файл печати» в товарах, а не только содержимое своей вкладки.
  useEffect(() => {
    if (!isOpen || printFiles !== null || designCode == null) return
    let cancelled = false
    void designsV2API
      .getProductionFiles(designCode)
      .then((data) => {
        if (!cancelled) setPrintFiles(data)
      })
      .catch(() => {
        if (!cancelled) setPrintFiles({ files: [], errors: [], warnings: [] })
      })
    return () => {
      cancelled = true
    }
  }, [isOpen, printFiles, designCode])

  // История — лениво: лента длинная, а открывают её редко

  useEffect(() => {
    if (!isOpen || tab !== 'history' || events !== null || designCode == null) return
    let cancelled = false
    designsV2API
      .getDesignEvents(designCode)
      .then((raw) => {
        if (!cancelled) setEvents(raw.map(mapPipelineEvent))
      })
      .catch(() => {
        if (!cancelled) setEvents([])
      })
    return () => {
      cancelled = true
    }
  }, [isOpen, tab, events, designCode])

  // Лайтбокс закрывается при смене макета/закрытии карточки
  useEffect(() => {
    setLightbox(null)
  }, [designCode, isOpen])

  useEffect(() => {
    setPendingAction(null)
    setReturnComment('')
    setEditSourceOpen(false)
    setAiEditOpen(false)
    setRegenerateWizardOpen(false)
    setRegenerateSeoWizardOpen(false)
    setMarketFilter({ value: 'all', nonce: 0 })
  }, [designCode, isOpen])

  /** Изображения макета для лайтбокса (PDF открывается в новой вкладке) */
  const designImages = useMemo(() => {
    if (!design?.media) return []
    return design.media
      .filter((m) => m.type !== 'pdf')
      .map((m) => ({
        media: m,
        src: getMediaUrl(m, design.design_code),
        label: `#${design.design_code} · ${MEDIA_TYPE_RU[m.type] || m.type}${m.isMain === 1 ? ' · основное' : ''}`,
      }))
  }, [design])

  const mainImage = useMemo(
    () =>
      design?.media?.find((m) => m.isMain === 1 && m.type !== 'pdf') ||
      design?.media?.find((m) => m.type !== 'pdf'),
    [design],
  )

  const openImageLightbox = useCallback(
    (mediaId: unknown) =>
      setLightbox({
        items: designImages.map(({ src, label }) => ({ src, label })),
        index: Math.max(
          0,
          designImages.findIndex((item) => item.media.id === mediaId),
        ),
      }),
    [designImages],
  )

  /** Комплектность товаров: создано/ожидается + недостающие категории */
  const kit = useMemo<KitSummary | null>(() => {
    const c = design?.completeness
    if (!c || !Array.isArray(c.expectedCategories) || c.expectedCategories.length === 0) return null
    const expected = c.expectedCategories.reduce((sum, e) => sum + (Number(e.quantity) || 0), 0)
    const createdList = Array.isArray(c.createdProducts) ? c.createdProducts : []
    const createdByCat = new Map<number, number>()
    for (const p of createdList) {
      createdByCat.set(p.category_id, (createdByCat.get(p.category_id) || 0) + 1)
    }
    const missing = c.expectedCategories
      .map((e) => ({
        description: e.description || e.prefix,
        missing: (Number(e.quantity) || 0) - (createdByCat.get(e.category_id) || 0),
      }))
      .filter((e) => e.missing > 0)
    return { expected, created: createdList.length, missing, isComplete: c.isComplete === true }
  }, [design])

  const stageContext = useMemo<StageContext>(
    () => ({
      expectedProducts: kit?.expected ?? products?.length ?? 0,
      createdProducts: kit?.created ?? products?.length ?? 0,
      cards: cards?.length ?? 0,
    }),
    [kit, products, cards],
  )

  const plan = useMemo(
    () => stagePlan(design?.pipeline_status, design?.design_state),
    [design?.pipeline_status, design?.design_state],
  )
  const { inlinePlan, headerActions } = useMemo(
    () => splitDesignCardStageActions(plan),
    [plan],
  )

  /** Артикулы, у которых есть файл, реально уходящий в печать */
  const articlesWithPrintFiles = useMemo(
    () =>
      new Set(
        (printFiles?.files || [])
          .filter((file) => file.storagePath)
          .map((file) => file.article || '')
          .filter(Boolean),
      ),
    [printFiles],
  )

  /** Сохранение изменённых полей. skipReload — при уходе с карточки. */
  const handleSave = async (skipReload = false): Promise<boolean> => {
    if (!design) return false
    setSaving(true)
    try {
      // Отправляем только изменённые поля. Это не переписывает устаревшие
      // legacy-значения соседних колонок (в частности language) при смене бренда.
      const updates = computeUpdates(form, initialForm)
      if (!Object.keys(updates).length) {
        showToast('Нет изменений для сохранения', 'info')
        return true
      }
      await designsV2API.updateDesign(design.design_code, updates)
      showToast('Макет сохранён', 'success')
      // Форма считается чистой сразу — guard не сработает при уходе
      setInitialForm({ ...form })
      onSaved()
      if (!skipReload) void load(design.design_code)
      return true
    } catch (e: any) {
      showToast(
        `Не удалось сохранить: ${e?.response?.data?.error?.message || e?.message || 'ошибка'}`,
        'error',
      )
      return false
    } finally {
      setSaving(false)
    }
  }

  const handleToggleTag = async (tag: DesignTag, checked: boolean) => {
    if (!design) return
    const next = checked ? [...designTags, tag] : designTags.filter((t) => t.id !== tag.id)
    setDesignTags(next)
    try {
      await designsV2API.setDesignTags(
        design.design_code,
        next.map((t) => t.id),
      )
      onSaved()
    } catch {
      setDesignTags(designTags)
      showToast('Не удалось сохранить теги', 'error')
    }
  }

  /** Выполнение действия конвейера. Реальные действия, часть из них — наружу. */
  const runAction = async (action: StageAction): Promise<void> => {
    if (!design) return
    const code = String(design.design_code)
    setActionBusy(action.kind)
    try {
      switch (action.kind) {
        case 'accept': {
          const accepted = await pipelineAPI.acceptDesign(code)
          showToast(`Макет #${code} принят`, 'success')
          notifyCoverage(parseFunnelCoverage(accepted), { designCode: code })
          break
        }
        case 'return':
          await pipelineAPI.returnDesign(code, returnComment.trim())
          showToast(`Макет #${code} возвращён дизайнеру`, 'success')
          setReturnComment('')
          break
        case 'techCheck':
          await api.post(`/pipeline/${encodeURIComponent(code)}/tech-check`, {})
          showToast('Файлы проверены', 'success')
          break
        case 'regenerate':
          await pipelineAPI.regenerateDesign(code)
          showToast('Генерация визуалов запущена', 'success')
          break
        case 'regenerateProduction': {
          const started = await pipelineAPI.regenerateProductionFiles(code)
          const categories = Array.isArray(started?.production_jobs)
            ? started.production_jobs.length
            : 0
          showToast(
            categories
              ? `Файлы для печати создаются заново: категорий ${categories}`
              : 'Создание файлов для печати запущено',
            'success',
          )
          break
        }
        case 'retry': {
          const retry = routeDesignCardRetry(await pipelineAPI.retryDesign(code), code)
          if (retry.kind === 'publication_disabled') {
            showToast(retry.message, 'info')
            return
          }
          showToast(retry.message, 'success')
          break
        }
        case 'createProducts':
          await api.post('/products/create-from-designs', { design_codes: [code] })
          showToast('Товары создаются', 'success')
          break
        case 'recalcKit':
          await api.post('/designs/recalculate-completeness', { design_codes: [code] })
          showToast('Комплектность пересчитана', 'success')
          break
      }
      setPendingAction(null)
      onSaved()
      void load(design.design_code)
    } catch (e: any) {
      showToast(
        `Не удалось выполнить: ${e?.response?.data?.error?.message || e?.message || 'ошибка'}`,
        'error',
      )
    } finally {
      setActionBusy(null)
    }
  }

  /** Клик по кнопке действия: спросить подтверждение или выполнить сразу */
  const handleStageAction = (action: StageAction) => {
    // Перегенерация визуалов спрашивает не «точно?», а что именно перегенерировать:
    // товары, кабинеты и контент-воронки. Один вход для всех кнопок стадий —
    // иначе одно и то же действие делало бы разное в зависимости от места клика.
    if (action.kind === 'regenerate') {
      setRegenerateWizardOpen(true)
      return
    }
    if (action.confirm || action.requiresComment) {
      setReturnComment('')
      setPendingAction(action)
      return
    }
    void runAction(action)
  }

  const copyToClipboard = async (text: string, message: string) => {
    // Общий путь копирования: с запасным вариантом, если буфер обмена отказал.
    if (await copyTextToClipboard(text)) showToast(message, 'success')
    else showToast('Не удалось скопировать', 'error')
  }

  const openPrintFilePreview = (file: ProductionFile, variant: ProductionFileVariant) => {
    const preview = resolvePrintFilePreview(printFiles?.files || [], file, variant)
    if (preview.kind === 'lightbox') {
      setLightbox({ items: preview.items, index: preview.index })
      return
    }
    if (!preview.source) {
      showToast('Файл недоступен', 'error')
      return
    }
    void openAuthorizedMedia(preview.source).catch(() => {
      showToast('Не удалось открыть файл', 'error')
    })
  }

  // ── Производные данные для каркаса (хуки — до раннего выхода) ──────────────

  const articles = useMemo(() => products?.map((product) => product.article) ?? null, [products])

  /** Префикс и название носителя по артикулу: из комплектности макета, затем из файлов печати. */
  const articleMeta = useMemo<Record<string, ProductArticleMeta>>(() => {
    const meta: Record<string, ProductArticleMeta> = {}
    for (const created of design?.completeness?.createdProducts ?? []) {
      meta[created.article] = {
        prefix: created.prefix || null,
        categoryName: created.description || null,
      }
    }
    for (const file of printFiles?.files ?? []) {
      if (!file.article || !file.categoryPrefix) continue
      meta[file.article] = {
        prefix: meta[file.article]?.prefix ?? file.categoryPrefix,
        categoryName: meta[file.article]?.categoryName ?? null,
      }
    }
    for (const product of products ?? []) {
      const current = meta[product.article]
      if (current && !current.categoryName && product.category) {
        meta[product.article] = { ...current, categoryName: product.category }
      } else if (!current && product.category) {
        meta[product.article] = { prefix: null, categoryName: product.category }
      }
    }
    return meta
  }, [design, printFiles, products])

  /** Название носителя по префиксу категории. */
  const categoryNames = useMemo<Record<string, string>>(() => {
    const names: Record<string, string> = {}
    for (const expected of design?.completeness?.expectedCategories ?? []) {
      if (expected.prefix && expected.description) names[expected.prefix] = expected.description
    }
    for (const meta of Object.values(articleMeta)) {
      if (meta.prefix && meta.categoryName && !names[meta.prefix]) names[meta.prefix] = meta.categoryName
    }
    return names
  }, [design, articleMeta])

  const prefixOf = useCallback(
    (article: string): string | null => articleMeta[article]?.prefix ?? null,
    [articleMeta],
  )

  if (!isOpen) return null

  const stage = pipelineStageInfo(design?.pipeline_status)
  // Состояние и стадия часто совпадают по названию («На приёмке», «Ошибка») —
  // два одинаковых бейджа подряд ничего не добавляют.
  const stateLabel = design ? designStateLabel(design.design_state) : ''
  const showStage = Boolean(stage && stage.label !== stateLabel)
  const tech = techSummary(design?.tech_check)
  const notice = designPipelineNotice(design?.pipeline_error, design?.pipeline_status)

  const code = designCode != null ? String(designCode) : ''
  const designLink = `${window.location.origin}/designs?design=${code}`
  /** Данные ещё не пришли для этого макета: каркас рисует скелетоны по зонам. */
  const shellLoading = loading && (!design || String(design.design_code) !== code)
  const canRegenerateProduction = permissions.includes('Pipeline:UPDATE')
  const canCreateProducts = permissions.includes('Product:CREATE')

  // ── Цепочка ────────────────────────────────────────────────────────────────

  const aggregates = channelAggregates(cards ?? [], articles ?? [])
  const goMarketplace = (value: OverviewFilter) => {
    setMarketFilter((current) => ({ value, nonce: current.nonce + 1 }))
    setTab('marketplace')
  }
  const chainItems: ChainItem[] = [
    design
      ? {
          key: 'theme',
          label: `Тематика № ${design.theme_number}`,
          tone: 'ok',
          title: 'Открыть тематику',
          onClick: () => onOpenTheme(design.theme_number),
        }
      : { key: 'theme', label: 'Тематика', tone: 'none', disabled: true, title: 'загружаем макет' },
    {
      key: 'design',
      label: 'Макет',
      sub: `#${code}`,
      current: true,
      tone: design?.pipeline_status === 'error' ? 'bad' : 'ok',
    },
    {
      key: 'products',
      label: products ? `Товары ${products.length}` : 'Товары',
      tone: products === null ? 'none' : kit && !kit.isComplete ? 'warn' : 'ok',
      title: 'Перейти на вкладку «Товары»',
      onClick: () => setTab('products'),
    },
    {
      key: 'presence',
      kind: 'marketplaces',
      items: PRESENCE_CHANNELS.map((channel) => {
        const aggregate = aggregates[channel]
        const label =
          channel === 'site'
            ? PRESENCE_CHANNEL_LABEL[channel]
            : `${PRESENCE_CHANNEL_LABEL[channel]} ${aggregate.live}/${aggregate.total}`
        if (cards === null) {
          return { key: channel, label, tone: 'none' as const, disabled: true, title: 'Загружаем карточки' }
        }
        return {
          key: channel,
          label,
          tone: aggregate.tone,
          title: aggregate.title,
          disabled: aggregate.withCards === 0,
          onClick: () => goMarketplace(channel === 'site' ? 'all' : channel),
        }
      }),
    },
  ]

  // ── Меню «Ещё» ─────────────────────────────────────────────────────────────

  const regenerateVisuals = headerActions.find((action) => action.kind === 'regenerate')
  const regenerateProduction = headerActions.find((action) => action.kind === 'regenerateProduction')
  const regenerateItems: EntityMoreMenuItem[] = [
    ...(regenerateVisuals
      ? [{
          key: 'regenerate-visuals',
          label: 'Перегенерировать визуалы',
          icon: <Replace className="h-4 w-4" />,
          disabled: actionBusy != null,
          onClick: () => handleStageAction(regenerateVisuals),
        }]
      : []),
    ...(canRegenerateSeo
      ? [{
          key: 'regenerate-seo',
          label: 'Перегенерировать тексты SEO',
          icon: <RefreshCw className="h-4 w-4" />,
          onClick: () => guardLeave(() => setRegenerateSeoWizardOpen(true)),
        }]
      : []),
    ...(regenerateProduction
      ? [{
          key: 'regenerate-production',
          label: 'Перегенерировать файлы печати',
          icon: <Replace className="h-4 w-4" />,
          disabled: actionBusy != null,
          onClick: () => handleStageAction(regenerateProduction),
        }]
      : []),
  ]
  const moreMenu: EntityMoreMenuItem[] = [
    ...regenerateItems,
    ...(regenerateItems.length > 0 ? [{ divider: true as const }] : []),
    {
      key: 'copy-code',
      label: 'Скопировать код',
      icon: <Copy className="h-4 w-4" />,
      hotkey: 'C',
      onClick: () => void copyToClipboard(code, `Код #${code} скопирован`),
    },
    {
      key: 'copy-link',
      label: 'Скопировать ссылку',
      icon: <LinkIcon className="h-4 w-4" />,
      hotkey: 'Shift+C',
      onClick: () => void copyToClipboard(designLink, 'Ссылка скопирована'),
    },
    {
      key: 'duplicate',
      label: 'Дублировать макет',
      icon: <Files className="h-4 w-4" />,
      onClick: () => design && onDuplicate(design),
    },
    {
      key: 'delete',
      label: 'Удалить макет',
      icon: <Trash2 className="h-4 w-4" />,
      danger: true,
      onClick: () => design && onDelete(design),
    },
  ]

  // ── Вкладки ────────────────────────────────────────────────────────────────

  const beforeAcceptance =
    design?.pipeline_status === 'on_acceptance' || design?.pipeline_status === 'returned_to_designer'
  const printMissing =
    articles !== null &&
    printFiles !== null &&
    articles.length > 0 &&
    !beforeAcceptance &&
    articles.some((article) => !articlesWithPrintFiles.has(article))
  const tabAttention: Partial<Record<CardTab, 'warn' | 'bad'>> = {
    printFiles: printMissing ? 'warn' : undefined,
    products: kit && !kit.isComplete ? 'warn' : undefined,
    marketplace: (cards ?? []).some((card) => isBlocked(card)) ? 'bad' : undefined,
  }
  const visualsKnown = STAGES_WITH_VISUALS.has(design?.pipeline_status || '')

  const checks = buildDesignChecks({
    techCheck: design?.tech_check,
    form,
    kit,
    articles,
    printArticles: printFiles ? articlesWithPrintFiles : null,
    visuals,
    visualsKnown,
    cards,
    onGo: setTab,
  })

  const nextStepActions: NextStepAction[] = [
    {
      key: 'edit-source',
      label: 'Изменить макет',
      icon: <Replace className="h-4 w-4" />,
      variant: 'secondary',
      disabled: !design || loading,
      onClick: () => setEditSourceOpen(true),
    },
    {
      key: 'edit-source-ai',
      label: 'Изменить с ИИ',
      icon: <Sparkles className="h-4 w-4" />,
      variant: 'secondary',
      disabled: !design || loading,
      onClick: () => setAiEditOpen(true),
    },
  ]

  const openTarget = entityNav.available ? entityNav.open : undefined

  return (
    <>
      <EntityModalShell
        isOpen
        ariaLabel={`Макет ${code}`}
        keys={codes.map(String)}
        activeKey={designCode != null ? String(designCode) : null}
        onNavigate={(key) => guardLeave(() => onNavigate(Number(key)))}
        onClose={() => guardLeave(onClose)}
        canGoBack={canGoBack}
        onBack={onBack ? () => guardLeave(onBack) : undefined}
        suspendHotkeys={Boolean(
          lightbox ||
            pendingLeave ||
            pendingAction ||
            regenerateWizardOpen ||
            regenerateSeoWizardOpen ||
            editSourceOpen,
        )}
        loading={shellLoading}
        kind={{ label: 'Макет', icon: Palette }}
        primaryId={{ value: code, label: 'код макета', link: designLink }}
        badges={
          design ? (
            <>
              <Badge variant={designStateVariant(design.design_state)}>{stateLabel}</Badge>
              {showStage && <Badge variant="blue">{stage!.label}</Badge>}
            </>
          ) : undefined
        }
        title={design ? designTitle(design) : 'Загрузка…'}
        meta={
          design
            ? [
                {
                  label: 'Тематика №',
                  value: String(design.theme_number),
                  mono: true,
                  title: 'Открыть тематику',
                  onClick: () => onOpenTheme(design.theme_number),
                },
                { label: 'Тип', value: design.type },
                { label: 'Создан', value: formatDateTime(design.created_at) },
              ]
            : undefined
        }
        moreMenu={moreMenu}
        chain={
          <EntityChain
            items={chainItems}
            onBack={canGoBack && onBack ? () => guardLeave(onBack) : undefined}
          />
        }
        nextStep={
          <StageActionBar
            plan={inlinePlan}
            context={stageContext}
            busy={actionBusy != null}
            busyKind={actionBusy}
            onRun={handleStageAction}
            notice={notice}
            rawError={design?.pipeline_error}
            prefixOf={prefixOf}
            onOpenProduct={onOpenProduct}
            loading={loading && !design}
            beforePrimary={nextStepActions}
            checks={checks}
          />
        }
        onSave={() => {
          if (!saving && !loading) void handleSave()
        }}
        tabsHotkeys={{
          keys: CARD_TABS.map((item) => item.key),
          onSelect: (key) => setTab(key as CardTab),
        }}
        tabs={
          <UnderlineTabs<CardTab>
            tabs={CARD_TABS.map((item) => {
              const attention = tabAttention[item.key]
              if (item.key === 'products') return { ...item, count: products?.length, attention }
              if (item.key === 'marketplace') return { ...item, count: cards?.length, attention }
              // На вкладке считаем печатные файлы, промежуточные не в счёт
              if (item.key === 'printFiles') return { ...item, count: articlesWithPrintFiles.size, attention }
              return { ...item, attention }
            })}
            active={tab}
            onChange={(key) => {
              // Прямой клик по вкладке сбрасывает фильтр площадки, заданный цепочкой.
              if (key === 'marketplace' && tab !== 'marketplace') {
                setMarketFilter((current) => ({ value: 'all', nonce: current.nonce + 1 }))
              }
              setTab(key)
            }}
            ariaLabel="Разделы карточки макета"
            panelId="design-card-tabpanel"
          />
        }
        sidebar={
          <ShellLayoutProbe>
            {(narrow) => (
          <div className="flex flex-col gap-3" data-layout={narrow ? "compact" : "full"}>
            {/* Узкое окно: превью меньше и рядом с цифрами, как у колонки карточек площадок */}
            <div className={narrow ? "flex items-start gap-3" : "contents"}>
            <div className={narrow ? "w-[6.5rem] shrink-0 space-y-2" : "space-y-3"}>
            {mainImage && design ? (
              <button
                type="button"
                title="Открыть в полном размере"
                aria-label="Открыть макет в полном размере"
                onClick={() => openImageLightbox(mainImage.id)}
                className={cn(
                  "group relative mx-auto flex aspect-[210/297] w-full shrink-0 cursor-zoom-in items-center justify-center overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm transition-all hover:border-gray-300 hover:shadow-medium focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 dark:border-zinc-700 dark:bg-white dark:focus:ring-offset-zinc-900",
                  narrow ? "max-w-[6.5rem]" : "max-w-[16rem]",
                )}
              >
                <SafeImg
                  src={getMediaUrl(mainImage, design.design_code)}
                  alt={`Макет ${design.design_code}`}
                  thumbnailWidth={480}
                  // Главная картинка карточки макета — одна и заведомо в кадре сразу.
                  lazy={false}
                  className="h-full w-full object-contain"
                />
                <span className="pointer-events-none absolute bottom-3 right-3 inline-flex items-center gap-1.5 rounded-md bg-gray-950/75 px-2.5 py-1.5 text-xs font-medium text-white opacity-0 shadow-sm backdrop-blur transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100">
                  <ZoomIn className="h-3.5 w-3.5" />
                  Увеличить
                </span>
              </button>
            ) : (
              <div
                className={cn(
                  "mx-auto flex aspect-[210/297] w-full shrink-0 items-center justify-center overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm dark:border-zinc-700 dark:bg-white",
                  narrow ? "max-w-[6.5rem]" : "max-w-[16rem]",
                )}
              >
                <ImageOff className="h-10 w-10 text-gray-300 dark:text-zinc-600" />
              </div>
            )}

            {/* Файлы макета миниатюрами: по картинке понятнее, чем по слову «ФОТО» */}
            {design && (design.media || []).length > 1 && (
              <div className="flex flex-wrap justify-center gap-1.5">
                {(design.media || []).map((m) =>
                  m.type === 'pdf' ? (
                    // Прямая ссылка открыла бы вкладку без токена — гейтвей
                    // ответил бы 401, поэтому PDF тянем авторизованным запросом.
                    <button
                      key={m.id}
                      type="button"
                      title="Открыть исходный PDF"
                      onClick={() =>
                        void openAuthorizedMedia(getMediaUrl(m, design.design_code)).catch(() =>
                          showToast('Не удалось открыть PDF макета', 'error'),
                        )
                      }
                      className="inline-flex h-14 w-11 items-center justify-center rounded border border-gray-300 bg-white text-2xs font-semibold text-gray-600 hover:border-indigo-400 dark:border-zinc-700 dark:bg-zinc-800 dark:text-zinc-300"
                    >
                      PDF
                    </button>
                  ) : (
                    <button
                      key={m.id}
                      type="button"
                      title="Открыть в полном размере"
                      onClick={() => openImageLightbox(m.id)}
                      className="h-14 w-11 overflow-hidden rounded border border-gray-300 bg-white hover:border-indigo-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-zinc-700"
                    >
                      <SafeImg
                        src={getMediaUrl(m, design.design_code)}
                        alt={MEDIA_TYPE_RU[m.type] || m.type}
                        className="h-full w-full object-cover"
                        iconClass="h-3.5 w-3.5"
                      />
                    </button>
                  ),
                )}
              </div>
            )}

            </div>
            <div className={narrow ? "min-w-0 flex-1 space-y-2" : "contents"}>
            {/* Техконтроль словами; разбор по форматам — в подсказке */}
            {design && (
              <div
                title={techChips(design.tech_check)
                  .map(
                    (chip) =>
                      `${chip.label}: ${chip.state === 'ok' ? 'ок' : chip.state === 'fail' ? 'замечание' : 'не проверялось'}`,
                  )
                  .join(' · ')}
                className={cn(
                  'rounded-md px-2.5 py-1.5 text-center text-xs font-medium',
                  TECH_TONE_CLASSES[tech.tone],
                )}
              >
                {tech.text}
              </div>
            )}

            {/* Макет создан в генераторе макетов: откуда он взялся */}
            {design && (
              <AiLayoutSourceNote
                jobId={design.ai_layout_job_id}
                jobName={design.ai_layout_job_name}
                deleted={Boolean(design.ai_layout_job_deleted)}
                permissions={permissions}
                role={userRole}
              />
            )}

            {design && (
              <div>
                <div className={cn(SEC, 'mb-1.5')}>Идентификаторы</div>
                <IdMeta
                  items={[
                    { label: 'Код', value: code, mono: true, copy: true },
                    { label: 'Тематика', value: String(design.theme_number), mono: true, copy: true },
                  ]}
                />
              </div>
            )}

            </div>
            </div>

            <div>
              <div className={cn(SEC, 'mb-1.5')}>Теги</div>
              <div className="flex flex-wrap items-center gap-1">
                {designTags.map((t) => (
                  <span
                    key={t.id}
                    className={cn(
                      'inline-flex items-center gap-1 rounded px-1.5 py-px text-2xs font-medium',
                      tagChipClass(t.color),
                    )}
                  >
                    {t.value}
                    <button
                      type="button"
                      onClick={() => void handleToggleTag(t, false)}
                      className="opacity-60 hover:opacity-100"
                      aria-label={`Снять тег ${t.value}`}
                    >
                      <X className="h-2.5 w-2.5" />
                    </button>
                  </span>
                ))}
                <TagPicker
                  tags={tags}
                  selectedIds={designTags.map((t) => t.id)}
                  onToggle={(tag, checked) => void handleToggleTag(tag, checked)}
                  onCreate={onCreateTag}
                  trigger={(open) => (
                    <button
                      type="button"
                      onClick={open}
                      className="inline-flex items-center gap-0.5 rounded border border-dashed border-gray-300 px-1.5 py-px text-2xs text-gray-400 hover:border-indigo-500 hover:text-indigo-600 dark:border-zinc-600 dark:text-zinc-500 dark:hover:border-indigo-400 dark:hover:text-indigo-400"
                    >
                      <Plus className="h-2.5 w-2.5" /> Тег
                    </button>
                  )}
                />
                {!tagsAvailable && (
                  <span className="text-2xs text-gray-400 dark:text-zinc-500">теги недоступны</span>
                )}
              </div>
            </div>
          </div>
            )}
          </ShellLayoutProbe>
        }
        footer={
          <>
            {dirtyCount > 0 ? (
              <div
                className="flex min-w-0 items-center gap-1.5 text-xs text-amber-700 dark:text-amber-300"
                role="status"
                title={`Изменено: ${dirtyFields.join(', ')}`}
              >
                <span className="h-1.5 w-1.5 rounded-full bg-amber-500" aria-hidden="true" />
                <span className="shrink-0">Изменено:</span>
                <span className="max-w-[32rem] truncate font-medium text-gray-700 dark:text-zinc-200">
                  {dirtyFields.join(', ')}
                </span>
              </div>
            ) : (
              <span className="text-xs text-gray-400 dark:text-zinc-500">
                Правки атрибутов сохраняются кнопкой справа или по Ctrl+S
              </span>
            )}
            <div className="ml-auto flex flex-wrap items-center justify-end gap-2">
              <Button variant="secondary" size="sm" onClick={() => guardLeave(onClose)}>
                Закрыть
              </Button>
              <Button
                size="sm"
                loading={saving}
                disabled={dirtyCount === 0 || loading}
                onClick={() => void handleSave()}
              >
                Сохранить изменения
              </Button>
            </div>
          </>
        }
      >
        <div
          id="design-card-tabpanel"
          role="tabpanel"
          aria-label={CARD_TABS.find((item) => item.key === tab)?.label}
        >
          {loading && (
            <div className="flex flex-col gap-3">
              <Skeleton variant="text" width="40%" />
              <Skeleton variant="custom" className="rounded-md" height={36} />
              <Skeleton variant="custom" className="rounded-md" height={36} />
              <Skeleton variant="custom" className="rounded-md" height={36} />
            </div>
          )}

          {!loading && design && tab === 'attrs' && (
            <AttributesTab
              design={design}
              form={form}
              setForm={setForm}
              dirtyFields={dirtyFieldKeys}
              dictionaries={dictionaries}
              onGenerateAi={() => onOpenAi(design)}
            />
          )}

          {!loading && design && tab === 'printFiles' && (
            <PrintFilesTab
              data={printFiles}
              beforeAcceptance={beforeAcceptance}
              onPreview={openPrintFilePreview}
              products={products?.map(({ article }) => ({ article })) ?? null}
              articleMeta={articleMeta}
              categoryNames={categoryNames}
              onRegenerate={() => handleStageAction(STANDALONE_ACTIONS.regenerateProduction)}
              regenerateDisabledReason={
                canRegenerateProduction ? undefined : 'Нужно право на изменение конвейера (Pipeline:UPDATE)'
              }
              regenerating={actionBusy === 'regenerateProduction'}
              onOpenProduct={onOpenProduct}
            />
          )}

          {!loading && design && tab === 'products' && (
            <ProductsTab
              kit={kit}
              products={products}
              articlesWithPrintFiles={articlesWithPrintFiles}
              error={productsError}
              onOpenProduct={onOpenProduct}
              cards={cards}
              visuals={visuals}
              visualsKnown={visualsKnown}
              designPreview={mainImage ? getMediaUrl(mainImage, design.design_code) : null}
              articleMeta={articleMeta}
              onCreateMissing={() => handleStageAction(STANDALONE_ACTIONS.createProducts)}
              createDisabledReason={
                canCreateProducts ? undefined : 'Нужно право на создание товаров (Product:CREATE)'
              }
              creating={actionBusy === 'createProducts'}
              canResendPhotos={canResendPhotos}
              onOpenTarget={openTarget}
            />
          )}

          {!loading && design && tab === 'marketplace' && (
            <MarketplaceTab
              key={marketFilter.nonce}
              cards={cards}
              visuals={visuals}
              fieldLabels={cardFieldLabels}
              pipelineStatus={design.pipeline_status}
              designCode={String(design.design_code)}
              canResendPhotos={canResendPhotos}
              initialFilter={marketFilter.value}
              onOpenVisual={(urls, idx, article) =>
                setLightbox({
                  items: urls.map((src, i) => ({
                    src,
                    label: `${article} · визуал ${i + 1}`,
                  })),
                  index: idx,
                })
              }
            />
          )}

          {!loading && design && tab === 'history' && (
            <HistoryTab
              design={design}
              events={events}
              onOpenProduct={onOpenProduct}
              prefixOf={prefixOf}
            />
          )}
        </div>
      </EntityModalShell>

      {designCode != null && (
        <EditDesignSourceModal
          isOpen={editSourceOpen}
          designCode={designCode}
          currentPreviewUrl={mainImage && design ? getMediaUrl(mainImage, design.design_code) : null}
          cardStatuses={(cards || []).map((card) => card.status)}
          onClose={() => setEditSourceOpen(false)}
          onStarted={() => {
            setEditSourceOpen(false)
            setCards(null)
            void load(designCode)
            onSaved()
          }}
        />
      )}

      {designCode != null && (
        <AiEditDesignModal
          isOpen={aiEditOpen}
          designCode={designCode}
          designType={design?.type}
          currentPreviewUrl={mainImage && design ? getMediaUrl(mainImage, design.design_code) : null}
          cardStatuses={(cards || []).map((card) => card.status)}
          onClose={() => setAiEditOpen(false)}
          onStarted={() => {
            setAiEditOpen(false)
            setCards(null)
            void load(designCode)
            onSaved()
          }}
        />
      )}

      <RegenerateVisualsWizard
        isOpen={regenerateWizardOpen}
        designCode={designCode}
        onClose={() => setRegenerateWizardOpen(false)}
        onLaunched={() => {
          // Прогон уже стартовал: макет ушёл в generating, а карточки МП
          // получат новые ревизии — читаем всё заново.
          setCards(null)
          setVisuals(null)
          onSaved()
          if (designCode != null) void load(designCode)
        }}
      />

      <RegenerateSeoWizard
        isOpen={regenerateSeoWizardOpen}
        designCode={designCode}
        canRenameTheme={canRenameTheme}
        onClose={() => setRegenerateSeoWizardOpen(false)}
        onSaved={() => {
          setCards(null)
          setVisuals(null)
          onSaved()
          if (designCode != null) void load(designCode)
        }}
      />

      {/* Guard несохранённых изменений */}
      <Modal
        isOpen={pendingLeave != null}
        onClose={() => setPendingLeave(null)}
        title="Несохранённые изменения"
        size="md"
        layer="nested"
        footer={
          <>
            <Button variant="secondary" onClick={() => setPendingLeave(null)}>
              Отмена
            </Button>
            <Button
              variant="ghost"
              className="text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950/50"
              onClick={() => {
                const action = pendingLeave
                setPendingLeave(null)
                action?.()
              }}
            >
              Не сохранять
            </Button>
            <Button
              loading={saving}
              onClick={() => {
                const action = pendingLeave
                void handleSave(true).then((ok) => {
                  if (ok) {
                    setPendingLeave(null)
                    action?.()
                  }
                })
              }}
            >
              Сохранить и продолжить
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <p>Сохранить изменения перед продолжением?</p>
          <div className="rounded-md bg-amber-50 px-3 py-2.5 dark:bg-amber-950/30">
            <div className="text-xs font-medium text-amber-800 dark:text-amber-300">
              {dirtyCount === 1 ? 'Изменено поле' : `Изменено полей: ${dirtyCount}`}
            </div>
            <ul className="mt-1.5 list-inside list-disc text-xs text-amber-700 dark:text-amber-400">
              {dirtyFields.map((field) => (
                <li key={field}>{field}</li>
              ))}
            </ul>
          </div>
        </div>
      </Modal>

      {/* Возврат дизайнеру — единственное действие, которому нужен ввод */}
      <Modal
        isOpen={pendingAction?.requiresComment === true}
        onClose={() => setPendingAction(null)}
        title="Вернуть макет дизайнеру"
        size="md"
        layer="nested"
        footer={
          <>
            <Button variant="secondary" onClick={() => setPendingAction(null)}>
              Отмена
            </Button>
            <Button
              variant="danger"
              loading={actionBusy === 'return'}
              disabled={returnComment.trim().length < 3}
              onClick={() => pendingAction && void runAction(pendingAction)}
            >
              Вернуть
            </Button>
          </>
        }
      >
        <div className="space-y-2">
          <p className="text-sm text-gray-700 dark:text-zinc-200">
            Дизайнер увидит только этот комментарий — напишите, что именно исправить.
          </p>
          <textarea
            rows={4}
            autoFocus
            value={returnComment}
            onChange={(e) => setReturnComment(e.target.value)}
            placeholder="Например: на макете обрезан хвост персонажа, поправьте отступ снизу"
            className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500 dark:border-zinc-700 dark:bg-zinc-900 dark:text-zinc-100"
          />
          {returnComment.trim().length < 3 && (
            <p className="text-xs text-gray-500 dark:text-zinc-400">
              Комментарий обязателен — минимум 3 символа.
            </p>
          )}
        </div>
      </Modal>

      {/* Подтверждение остальных действий конвейера */}
      <ConfirmDialog
        isOpen={pendingAction != null && !pendingAction.requiresComment}
        title={pendingAction?.confirm?.title || 'Подтвердите действие'}
        message={
          pendingAction?.confirm?.message(String(designCode ?? ''), stageContext) ||
          'Выполнить действие?'
        }
        confirmLabel={pendingAction?.label || 'Выполнить'}
        danger={pendingAction?.variant === 'danger'}
        loading={actionBusy != null}
        onConfirm={() => pendingAction && void runAction(pendingAction)}
        onClose={() => {
          setPendingAction(null)
        }}
      />

      {/* Полноэкранный просмотр изображений */}
      <Lightbox
        isOpen={lightbox != null}
        items={lightbox?.items || []}
        index={lightbox?.index ?? 0}
        onIndexChange={(index) => setLightbox((prev) => (prev ? { ...prev, index } : prev))}
        onClose={() => setLightbox(null)}
      />
    </>
  )
}

export default DesignCardModal
